import type { Role } from '@atlas/api-contract';
import { Link } from '@tanstack/react-router';
import { LogOut, Users } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { useCurrentUser, useLogout } from './auth-queries';

/** Human-readable role label for the menu header. */
const ROLE_LABEL: Record<Role, string> = {
  ADMIN: 'Admin',
  DESIGNER: 'Designer',
  COMMENTER: 'Commenter',
};

/** The first letter of the name, for the avatar fallback. */
const initial = (name: string): string =>
  name.trim().charAt(0).toUpperCase() || '?';

/**
 * The header's account control (spec R7).
 *
 * Replaces the Phase 1 display-name menu: identity now comes from the session
 * rather than from a name typed into `localStorage`. Shows who is signed in and
 * their role, links an Admin to the Users screen, and signs out. Quiet by
 * design, like everything else in the header.
 */
export const UserMenu = () => {
  const { user, isAdmin } = useCurrentUser();
  const logout = useLogout();

  if (user === null) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 px-1.5 text-muted-foreground hover:text-foreground"
        >
          <Avatar className="size-6">
            {user.avatarUrl === null ? null : (
              <AvatarImage src={user.avatarUrl} alt="" />
            )}
            <AvatarFallback className="bg-secondary text-[10px] font-semibold text-secondary-foreground">
              {initial(user.name)}
            </AvatarFallback>
          </Avatar>
          <span className="hidden max-w-[10rem] truncate sm:inline">
            {user.name}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="truncate font-medium text-foreground">
            {user.name}
          </span>
          <span className="truncate text-xs font-normal text-muted-foreground">
            {user.email}
          </span>
          <span className="mt-1 text-xs font-normal text-muted-foreground">
            {ROLE_LABEL[user.role]}
          </span>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {isAdmin ? (
          <>
            <DropdownMenuItem asChild>
              <Link to="/users">
                <Users aria-hidden="true" />
                Users
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}

        <DropdownMenuItem
          disabled={logout.isPending}
          onSelect={(event) => {
            event.preventDefault();
            logout.mutate();
          }}
        >
          <LogOut aria-hidden="true" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
